import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Activity, 
  ArrowRight, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Search,
  Filter,
  Calendar,
  FileText,
  ShoppingCart,
  Package,
  DollarSign,
  User,
  Eye,
  Download,
  RefreshCw,
  BookOpen,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Home,
  GraduationCap,
  Target,
  TrendingUp,
  Shield,
  Settings,
  BarChart3,
  Calculator,
  CreditCard,
  Building,
  Users,
  Briefcase,
  Globe,
  Award,
  Zap,
  Copy,
  MoreHorizontal,
  Filter as FilterIcon,
  SortAsc,
  SortDesc,
  FileCheck,
  FileX,
  FileClock,
  ArrowUpDown,
  CalendarDays,
  Database,
  GitBranch,
  GitCommit,
  GitPullRequest,
  GitMerge,
  GitCompare,
  Play
} from 'lucide-react';
import { formatDateTime, formatCurrency } from '../utils/formatters';

const ProcessFlowTracker = () => {
  // State management
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, flows, audit, templates, tasks
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7d'); // 7d, 30d, 90d, custom
  const [sortField, setSortField] = useState('initiated_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [processFlows, setProcessFlows] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tasks (Teaching) state
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskSearchTerm, setTaskSearchTerm] = useState('');

  // Document Converted state
  const [documentConversions, setDocumentConversions] = useState([]);
  const [conversionFilters, setConversionFilters] = useState({
    sourceModule: '',
    targetModule: '',
    status: '',
    dateRange: 'all',
    processType: ''
  });
  const [conversionSearchTerm, setConversionSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedConversion, setSelectedConversion] = useState(null);
  const [showConversionDetail, setShowConversionDetail] = useState(false);

  // Business Cycle state
  const [selectedCycle, setSelectedCycle] = useState('sales');
  const [showCycleDetails, setShowCycleDetails] = useState(true);

  // Teaching tasks data structure
  const teachingTasks = {
    'business-foundation': {
      title: '企業基礎 (Business Foundation)',
      icon: Building,
      color: 'blue',
      description: '企業成立、組織結構和基礎會計概念',
      chapters: [
        {
          id: 'chapter-1',
          title: '第一章：開設中小企業',
          subtitle: 'Starting a Small and Medium-sized Enterprise',
          tasks: [
            { id: '1.1', title: '會計專業概覽', module: 'financial', description: 'Overview of Accounting Profession' },
            { id: '1.2', title: '香港中小企業的發展', module: 'dashboard', description: 'Development of SMEs in Hong Kong' },
            { id: '1.3', title: '電腦化會計系統對營運中小企業的重要性', module: 'dashboard', description: 'Importance of Computerized Accounting Systems for SME Operations' },
            { id: '1.4', title: 'MYOB', module: 'dashboard', description: 'MYOB Accounting Software' },
            { id: '1.5', title: '企業組織的類型', module: 'dashboard', description: 'Types of Business Organizations' },
            { id: '1.6', title: '成立企業的不同資金來源', module: 'financial', description: 'Different Sources of Funding for Business Establishment' },
            { id: '1.7', title: '資產、負債及資本', module: 'chart-of-accounts', description: 'Assets, Liabilities, and Capital' },
            { id: '1.8', title: '記錄及處理資產、負債及資本', module: 'chart-of-accounts', description: 'Recording and Processing Assets, Liabilities, and Capital' },
            { id: '1.9', title: '貨幣量度單位概念', module: 'financial', description: 'Concept of Monetary Measurement Unit' }
          ]
        },
        {
          id: 'chapter-2',
          title: '第二章：企業開業前',
          subtitle: 'Before Business Commencement',
          tasks: [
            { id: '2.1', title: '成立中小型獨資企業須考慮的要素', module: 'dashboard', description: 'Factors to Consider When Establishing a Small and Medium-sized Sole Proprietorship' },
            { id: '2.2', title: '供應商的資料', module: 'purchase', description: 'Supplier Information' },
            { id: '2.3', title: '聯絡供應商', module: 'purchase', description: 'Contacting Suppliers' },
            { id: '2.4', title: '非流動資產', module: 'chart-of-accounts', description: 'Non-current Assets' },
            { id: '2.5', title: '建立非流動資產登記冊', module: 'chart-of-accounts', description: 'Establishing a Non-current Asset Register' },
            { id: '2.6', title: '記錄購買非流動資產', module: 'chart-of-accounts', description: 'Recording the Purchase of Non-current Assets' },
            { id: '2.7', title: '東主投資於企業的非流動資產', module: 'chart-of-accounts', description: 'Owner\'s Investment in Non-current Assets of the Business' },
            { id: '2.8', title: '折舊的概念', module: 'chart-of-accounts', description: 'Concept of Depreciation' },
            { id: '2.9', title: '購買或租賃非流動資產的理由', module: 'chart-of-accounts', description: 'Reasons for Purchasing or Leasing Non-current Assets' }
          ]
        }
      ]
    },
    'daily-transactions': {
      title: '日常交易事項 (Daily Transactions)',
      icon: RefreshCw,
      color: 'green',
      description: '日常業務交易處理和記錄',
      chapters: [
        {
          id: 'chapter-3',
          title: '第三章：日常交易事項-購貨',
          subtitle: 'Daily Transactions - Purchases',
          tasks: [
            { id: '3.1', title: '購貨', module: 'purchase', description: 'Purchases' },
            { id: '3.2', title: '購貨流程及所需文件', module: 'purchase', description: 'Purchase Process and Required Documents' },
            { id: '3.3', title: '供應商月結單', module: 'purchase', description: 'Supplier Monthly Statement' }
          ]
        },
        {
          id: 'chapter-4',
          title: '第四章：日常交易記錄-購貨退出',
          subtitle: 'Daily Transaction Records - Purchase Returns',
          tasks: [
            { id: '4.1', title: '退貨(購貨退出)的步驟及原因', module: 'purchase', description: 'Steps and Reasons for Returns (Purchase Returns)' },
            { id: '4.2', title: '借項通知單', module: 'purchase', description: 'Debit Note' },
            { id: '4.3', title: '貸項通知單', module: 'purchase', description: 'Credit Note' },
            { id: '4.4', title: '會計記錄購貨退出項目', module: 'purchase', description: 'Accounting Records for Purchase Return Items' }
          ]
        },
        {
          id: 'chapter-5',
          title: '第五章：日常交易事項-付款給購貨債權人',
          subtitle: 'Daily Transactions - Payment to Creditors for Purchases',
          tasks: [
            { id: '5.1', title: '應付帳款的總數', module: 'financial', description: 'Total Accounts Payable' },
            { id: '5.2', title: '購貨折扣', module: 'purchase', description: 'Purchase Discount' },
            { id: '5.3', title: '記錄支付購貨債權人的現金及購貨現金折扣', module: 'financial', description: 'Recording Cash Payment to Creditors for Purchases and Purchase Cash Discount' }
          ]
        },
        {
          id: 'chapter-6',
          title: '第六章：日常交易事項-銷貨',
          subtitle: 'Daily Transactions - Sales',
          tasks: [
            { id: '6.1', title: '銷貨', module: 'quotation', description: 'Sales' },
            { id: '6.2', title: '銷貨流程及所需文件', module: 'quotation', description: 'Sales Process and Required Documents' },
            { id: '6.3', title: '記錄銷貨交易', module: 'sales-orders', description: 'Recording Sales Transactions' },
            { id: '6.4', title: '收款', module: 'financial', description: 'Collections/Receipts' }
          ]
        },
        {
          id: 'chapter-7',
          title: '第七章：日常交易事項-銷貨退回',
          subtitle: 'Daily Transactions - Sales Returns',
          tasks: [
            { id: '7.1', title: '銷貨退回', module: 'sales-orders', description: 'Sales Returns' },
            { id: '7.2', title: '貸項通知單', module: 'sales-orders', description: 'Credit Note' },
            { id: '7.3', title: '記錄銷貨退回項目', module: 'sales-orders', description: 'Recording Sales Return Items' }
          ]
        },
        {
          id: 'chapter-8',
          title: '第八章：日常交易事項-收到銷貨債務人的貨款',
          subtitle: 'Daily Transactions - Receipt of Payment from Sales Debtors',
          tasks: [
            { id: '8.1', title: '應收款的總數', module: 'financial', description: 'Total Accounts Receivable' },
            { id: '8.2', title: '銷貨現金折扣', module: 'financial', description: 'Sales Cash Discount' }
          ]
        },
        {
          id: 'chapter-9',
          title: '第九章：日常交易事項-支付費用',
          subtitle: 'Daily Transactions - Payment of Expenses',
          tasks: [
            { id: '9.1', title: '費用', module: 'chart-of-accounts', description: 'Expenses' },
            { id: '9.2', title: '記錄及處理費用', module: 'financial', description: 'Recording and Processing Expenses' },
            { id: '9.3', title: '零用現金簿', module: 'financial', description: 'Petty Cash Book' }
          ]
        },
        {
          id: 'chapter-10',
          title: '第十章：日常交易事項-收取其他收益',
          subtitle: 'Daily Transactions - Receiving Other Income',
          tasks: [
            { id: '10.1', title: '收益', module: 'chart-of-accounts', description: 'Income' },
            { id: '10.2', title: '記錄及處理收益', module: 'financial', description: 'Recording and Processing Income' }
          ]
        }
      ]
    },
    'financial-management': {
      title: '財務管理 (Financial Management)',
      icon: DollarSign,
      color: 'purple',
      description: '財務報表、銀行往來和財務分析',
      chapters: [
        {
          id: 'chapter-11',
          title: '第十一章：收到銀行結單',
          subtitle: 'Receiving Bank Statements',
          tasks: [
            { id: '11.1', title: '銀行結單', module: 'financial', description: 'Bank Statement' },
            { id: '11.2', title: '現金簿與銀行結單餘額不相符的原因', module: 'financial', description: 'Reasons for Discrepancy Between Cash Book and Bank Statement Balances' },
            { id: '11.3', title: '銀行往來調節表', module: 'financial', description: 'Bank Reconciliation Statement' },
            { id: '11.4', title: '向有關人士傳達資料', module: 'financial', description: 'Communicating Information to Relevant Parties' },
            { id: '11.5', title: '解釋銀行往來調節表報告', module: 'financial', description: 'Explaining Bank Reconciliation Statement Report' }
          ]
        },
        {
          id: 'chapter-12',
          title: '第十二章：了解財務會計原則',
          subtitle: 'Understanding Financial Accounting Principles',
          tasks: [
            { id: '12.1', title: '財務報表', module: 'financial', description: 'Financial Statements' },
            { id: '12.2', title: '財務報表的功能', module: 'financial', description: 'Functions of Financial Statements' },
            { id: '12.3', title: '常用的概念、原則及慣例', module: 'financial', description: 'Common Concepts, Principles, and Conventions' },
            { id: '12.4', title: '編製財務報表的基本假設', module: 'financial', description: 'Basic Assumptions for Preparing Financial Statements' },
            { id: '12.5', title: '不依循會計概念的後果', module: 'financial', description: 'Consequences of Not Following Accounting Concepts' }
          ]
        }
      ]
    },
    'year-end-processes': {
      title: '年結處理 (Year-End Processes)',
      icon: Calendar,
      color: 'orange',
      description: '年結時的調整項目和財務報表編製',
      chapters: [
        {
          id: 'chapter-13',
          title: '第十三章：不能收回的應收帳款',
          subtitle: 'Unrecoverable Accounts Receivable',
          tasks: [
            { id: '13.1', title: '不能收回的應收帳款', module: 'financial', description: 'Unrecoverable Accounts Receivable' },
            { id: '13.2', title: '討債信', module: 'financial', description: 'Debt Collection Letters' },
            { id: '13.3', title: '記錄及處理壞帳', module: 'financial', description: 'Recording and Handling Bad Debts' },
            { id: '13.4', title: '減少壞帳的改善措施', module: 'financial', description: 'Improvement Measures to Reduce Bad Debts' }
          ]
        },
        {
          id: 'chapter-14',
          title: '第十四章：收回已註銷的應收帳款',
          subtitle: 'Recovery of Written-off Accounts Receivable',
          tasks: [
            { id: '14.1', title: '收回已註銷的應收帳款', module: 'financial', description: 'Recovery of Written-off Accounts Receivable' },
            { id: '14.2', title: '記錄及處理壞帳收回', module: 'financial', description: 'Recording and Handling Bad Debt Recovery' }
          ]
        },
        {
          id: 'chapter-15',
          title: '第十五章：檢視年結時的應收帳款',
          subtitle: 'Review of Accounts Receivable at Year-End',
          tasks: [
            { id: '15.1', title: '帳齡分析表', module: 'financial', description: 'Aging Analysis Report' },
            { id: '15.2', title: '應收帳款的減值虧損準備', module: 'financial', description: 'Impairment Loss Provision for Accounts Receivable' },
            { id: '15.3', title: '銷貨折扣準備', module: 'financial', description: 'Sales Discount Provision' },
            { id: '15.4', title: '營運資金管理', module: 'financial', description: 'Working Capital Management' },
            { id: '15.5', title: '風險管理', module: 'financial', description: 'Risk Management' }
          ]
        },
        {
          id: 'chapter-16',
          title: '第十六章：年結時使用中的資產折舊/減值',
          subtitle: 'Depreciation/Impairment of Assets in Use at Year-End',
          tasks: [
            { id: '16.1', title: '資產減值的因素', module: 'chart-of-accounts', description: 'Factors of Asset Impairment' },
            { id: '16.2', title: '定義', module: 'chart-of-accounts', description: 'Definitions' },
            { id: '16.3', title: '折舊的計算', module: 'chart-of-accounts', description: 'Calculation of Depreciation' },
            { id: '16.4', title: '記錄折舊費用', module: 'chart-of-accounts', description: 'Recording Depreciation Expense' }
          ]
        },
        {
          id: 'chapter-17',
          title: '第十七章：年結時盤點存貨及存貨計價',
          subtitle: 'Year-End Inventory Count and Inventory Valuation',
          tasks: [
            { id: '17.1', title: '期末存貨', module: 'inventory', description: 'Ending Inventory' },
            { id: '17.2', title: '盤點存貨', module: 'inventory', description: 'Inventory Count' },
            { id: '17.3', title: '存貨計價方法', module: 'inventory', description: 'Inventory Valuation Methods' },
            { id: '17.4', title: '記錄及處理期末存貨', module: 'inventory', description: 'Recording and Handling Ending Inventory' },
            { id: '17.5', title: '存貨計價與穩健保守原則', module: 'inventory', description: 'Inventory Valuation and Prudence Principle' }
          ]
        },
        {
          id: 'chapter-18',
          title: '第十八章：年結時的應計費用',
          subtitle: 'Accrued Expenses at Year-End',
          tasks: [
            { id: '18.1', title: '年結時的應計費用', module: 'financial', description: 'Accrued Expenses at Year-End' },
            { id: '18.2', title: '記錄及處理應計費用', module: 'financial', description: 'Recording and Handling Accrued Expenses' },
            { id: '18.3', title: '香港稅務', module: 'financial', description: 'Hong Kong Taxation' }
          ]
        },
        {
          id: 'chapter-19',
          title: '第十九章：年結時的預付費用',
          subtitle: 'Prepaid Expenses at Year-End',
          tasks: [
            { id: '19.1', title: '年結時的預付費用', module: 'financial', description: 'Prepaid Expenses at Year-End' },
            { id: '19.2', title: '記錄及處理預付費用', module: 'financial', description: 'Recording and Handling Prepaid Expenses' }
          ]
        },
        {
          id: 'chapter-20',
          title: '第二十章：年結時的應計收益',
          subtitle: 'Accrued Income at Year-End',
          tasks: [
            { id: '20.1', title: '年結時的應計收益', module: 'financial', description: 'Accrued Income at Year-End' },
            { id: '20.2', title: '記錄及處理應計收益', module: 'financial', description: 'Recording and Processing Accrued Income' }
          ]
        },
        {
          id: 'chapter-21',
          title: '第二十一章：年結時的預收收益',
          subtitle: 'Unearned Revenue at Year-End',
          tasks: [
            { id: '21.1', title: '年結時的預收收益', module: 'financial', description: 'Unearned Revenue at Year-End' },
            { id: '21.2', title: '記錄及處理預收收益', module: 'financial', description: 'Recording and Processing Unearned Revenue' }
          ]
        }
      ]
    },
    'advanced-accounting': {
      title: '進階會計 (Advanced Accounting)',
      icon: Calculator,
      color: 'red',
      description: '合夥企業、有限公司和財務分析',
      chapters: [
        {
          id: 'chapter-22',
          title: '第二十二章：東主提用貨物、現金或資產作私人用途',
          subtitle: 'Owner\'s Drawings of Goods, Cash, or Assets for Personal Use',
          tasks: [
            { id: '22.1', title: '提用', module: 'financial', description: 'Drawings' },
            { id: '22.2', title: '記錄及處理東主提用', module: 'financial', description: 'Recording and Processing Owner\'s Drawings' },
            { id: '22.3', title: '錯誤支付東主的私人費用', module: 'financial', description: 'Incorrect Payment of Owner\'s Personal Expenses' }
          ]
        },
        {
          id: 'chapter-23',
          title: '第二十三章：年結時發現入帳錯誤',
          subtitle: 'Discovery of Bookkeeping Errors at Year-End',
          tasks: [
            { id: '23.1', title: '檢視錯誤及漏記的交易項目', module: 'financial', description: 'Reviewing Errors and Omitted Transactions' },
            { id: '23.2', title: '不影響試算表平衡的錯誤', module: 'financial', description: 'Errors Not Affecting Trial Balance Equality' },
            { id: '23.3', title: '更正錯誤', module: 'financial', description: 'Correcting Errors' },
            { id: '23.4', title: '影響試算表平衡的錯誤', module: 'financial', description: 'Errors Affecting Trial Balance Equality' }
          ]
        },
        {
          id: 'chapter-24',
          title: '第二十四章：變賣及重置變舊的非流動資產',
          subtitle: 'Disposal and Replacement of Old Non-Current Assets',
          tasks: [
            { id: '24.1', title: '非流動資產的殘值與累積折舊', module: 'chart-of-accounts', description: 'Residual Value and Accumulated Depreciation of Non-Current Assets' },
            { id: '24.2', title: '非流動資產的變賣', module: 'chart-of-accounts', description: 'Disposal of Non-Current Assets' },
            { id: '24.3', title: '以舊換新的非流動資產的成本', module: 'chart-of-accounts', description: 'Cost of Replacing Old Non-Current Assets with New Ones' }
          ]
        },
        {
          id: 'chapter-25',
          title: '第二十五章：向有關人士提交財務報表',
          subtitle: 'Submission of Financial Statements to Relevant Parties',
          tasks: [
            { id: '25.1', title: '財務資料的使用者', module: 'financial', description: 'Users of Financial Information' },
            { id: '25.2', title: '編製財務報表', module: 'financial', description: 'Preparation of Financial Statements' },
            { id: '25.3', title: '現金流量表', module: 'financial', description: 'Cash Flow Statement' },
            { id: '25.4', title: '解釋財務報表報告', module: 'financial', description: 'Explanation of Financial Statement Reports' },
            { id: '25.5', title: '會計分期概念', module: 'financial', description: 'Accounting Period Concept' },
            { id: '25.6', title: '預算原則', module: 'financial', description: 'Budgeting Principles' }
          ]
        },
        {
          id: 'chapter-26',
          title: '第二十六章：企業及會計的道德問題',
          subtitle: 'Ethical Issues in Business and Accounting',
          tasks: [
            { id: '26.1', title: '道德的重要性', module: 'dashboard', description: 'Importance of Ethics' },
            { id: '26.2', title: '企業道德與社會責任', module: 'dashboard', description: 'Corporate Ethics and Social Responsibility' },
            { id: '26.3', title: '電腦化會計系統', module: 'dashboard', description: 'Computerized Accounting Systems' }
          ]
        },
        {
          id: 'chapter-27',
          title: '第二十七章：合夥企業的成立及變動',
          subtitle: 'Establishment and Changes of Partnership Enterprises',
          tasks: [
            { id: '27.1', title: '合夥企業及獨資經營', module: 'financial', description: 'Partnership Enterprises and Sole Proprietorships' },
            { id: '27.2', title: '合夥契約', module: 'financial', description: 'Partnership Agreement' },
            { id: '27.3', title: '損益分配帳', module: 'financial', description: 'Profit and Loss Distribution Account' },
            { id: '27.4', title: '合夥人的資本帳', module: 'financial', description: 'Partners\' Capital Account' },
            { id: '27.5', title: '合夥人的財務狀況表', module: 'financial', description: 'Partners\' Financial Statements' },
            { id: '27.6', title: '商譽', module: 'financial', description: 'Goodwill' },
            { id: '27.7', title: '資產重估', module: 'financial', description: 'Asset Revaluation' },
            { id: '27.8', title: '企業個體概念', module: 'financial', description: 'Business Entity Concept' },
            { id: '27.9', title: '繼續經營概念', module: 'financial', description: 'Going Concern Concept' }
          ]
        },
        {
          id: 'chapter-28',
          title: '第二十八章：合夥企業解散',
          subtitle: 'Dissolution of Partnership Enterprises',
          tasks: [
            { id: '28.1', title: '合夥企業解散', module: 'financial', description: 'Dissolution of Partnership Enterprises' },
            { id: '28.2', title: '合夥企業解散時須處理的事項', module: 'financial', description: 'Matters to be Dealt with upon Dissolution of Partnership Enterprises' },
            { id: '28.3', title: '變現報告', module: 'financial', description: 'Realization Report' },
            { id: '28.4', title: '企業個體概念', module: 'financial', description: 'Business Entity Concept' },
            { id: '28.5', title: '繼續經營概念', module: 'financial', description: 'Going Concern Concept' }
          ]
        },
        {
          id: 'chapter-29',
          title: '第二十九章：中小企業是有限公司',
          subtitle: 'Small and Medium-sized Enterprises are Limited Companies',
          tasks: [
            { id: '29.1', title: '有限公司', module: 'financial', description: 'Limited Company' },
            { id: '29.2', title: '股票與債券', module: 'financial', description: 'Stocks and Bonds' },
            { id: '29.3', title: '股票與股本', module: 'financial', description: 'Stocks and Share Capital' },
            { id: '29.4', title: '債券', module: 'financial', description: 'Bonds' },
            { id: '29.5', title: '債券利息與股息', module: 'financial', description: 'Bond Interest and Dividends' },
            { id: '29.6', title: '儲備轉撥', module: 'financial', description: 'Reserve Transfer' },
            { id: '29.7', title: '有限公司的財務報表', module: 'financial', description: 'Financial Statements of Limited Companies' },
            { id: '29.8', title: '企業個體概念', module: 'financial', description: 'Business Entity Concept' }
          ]
        }
      ]
    },
    'financial-analysis': {
      title: '財務分析 (Financial Analysis)',
      icon: BarChart3,
      color: 'indigo',
      description: '財務比率分析和企業評估',
      chapters: [
        {
          id: 'chapter-30',
          title: '第三十章：評估企業的盈利能力',
          subtitle: 'Assessing Enterprise Profitability',
          tasks: [
            { id: '30.1', title: '企業盈利能力', module: 'financial', description: 'Enterprise Profitability' },
            { id: '30.2', title: '企業盈利能力比率', module: 'financial', description: 'Enterprise Profitability Ratios' },
            { id: '30.3', title: '比較企業各個期間的盈利能力', module: 'financial', description: 'Comparing Enterprise Profitability Across Periods' },
            { id: '30.4', title: '比較同業的業績', module: 'financial', description: 'Comparing Performance with Peers' },
            { id: '30.5', title: '其他資料', module: 'financial', description: 'Other Information' },
            { id: '30.6', title: '傳達資料', module: 'financial', description: 'Communicating Information' },
            { id: '30.7', title: '建議改善方法', module: 'financial', description: 'Suggested Improvement Methods' }
          ]
        },
        {
          id: 'chapter-31',
          title: '第三十一章：評估企業的變現能力及效益',
          subtitle: 'Assessing Enterprise Liquidity and Benefits',
          tasks: [
            { id: '31.1', title: '企業變現能力', module: 'financial', description: 'Enterprise Liquidity' },
            { id: '31.2', title: '企業變現能力比率及資產運用效益', module: 'financial', description: 'Enterprise Liquidity Ratios and Asset Utilization Efficiency' },
            { id: '31.3', title: '評估企業的能力', module: 'financial', description: 'Assessing Enterprise Capability' },
            { id: '31.4', title: '比較企業各個期間的變現能力', module: 'financial', description: 'Comparing Enterprise Liquidity Across Periods' },
            { id: '31.5', title: '比較同業的業績', module: 'financial', description: 'Comparing Performance with Peers' },
            { id: '31.6', title: '比率隨時間變動的重要性及原因', module: 'financial', description: 'Importance and Reasons for Ratio Changes Over Time' },
            { id: '31.7', title: '兩間公司之間的比率差異', module: 'financial', description: 'Ratio Differences Between Two Companies' },
            { id: '31.8', title: '建議改善方法', module: 'financial', description: 'Suggested Improvement Methods' }
          ]
        }
      ]
    },
    'cost-accounting': {
      title: '成本會計 (Cost Accounting)',
      icon: Target,
      color: 'teal',
      description: '成本會計方法和預算控制',
      chapters: [
        {
          id: 'chapter-32',
          title: '第三十二章：成本會計的功能、分類與概念',
          subtitle: 'Functions, Classifications, and Concepts of Cost Accounting',
          tasks: [
            { id: '32.1', title: '成本會計、管理會計及財務會計', module: 'financial', description: 'Cost Accounting, Management Accounting, and Financial Accounting' },
            { id: '32.2', title: '成本的分類', module: 'financial', description: 'Classification of Costs' },
            { id: '32.3', title: '單位成本的重要性', module: 'financial', description: 'Importance of Unit Cost' }
          ]
        },
        {
          id: 'chapter-33',
          title: '第三十三章：成本會計法',
          subtitle: 'Cost Accounting Methods',
          tasks: [
            { id: '33.1', title: '成本會計法', module: 'financial', description: 'Cost Accounting Methods' },
            { id: '33.2', title: '分批成本法與成批成本法', module: 'financial', description: 'Job Order Costing and Batch Costing' },
            { id: '33.3', title: '分批成本法成本流程', module: 'financial', description: 'Job Order Costing Process' },
            { id: '33.4', title: '吸收成本法及邊際成本法', module: 'financial', description: 'Absorption Costing and Marginal Costing' }
          ]
        },
        {
          id: 'chapter-34',
          title: '第三十四章：預算及預算控制',
          subtitle: 'Budgeting and Budgetary Control',
          tasks: [
            { id: '34.1', title: '預算', module: 'financial', description: 'Budget' },
            { id: '34.2', title: '預算的種類', module: 'financial', description: 'Types of Budgets' },
            { id: '34.3', title: '現金預算編製', module: 'financial', description: 'Cash Budget Preparation' }
          ]
        },
        {
          id: 'chapter-35',
          title: '第三十五章：分析本量利關係',
          subtitle: 'Analysis of Cost-Volume-Profit Relationship',
          tasks: [
            { id: '35.1', title: '本量利分析', module: 'financial', description: 'Cost-Volume-Profit Analysis' },
            { id: '35.2', title: '損益平衡點', module: 'financial', description: 'Break-Even Point' },
            { id: '35.3', title: '邊際貢獻法', module: 'financial', description: 'Contribution Margin Method' },
            { id: '35.4', title: '成本產量及利潤關係圖', module: 'financial', description: 'Cost-Volume-Profit Graph' },
            { id: '35.5', title: '目標利潤', module: 'financial', description: 'Target Profit' }
          ]
        }
      ]
    }
  };

  // Navigation function to other modules
  const navigateToModule = (moduleName) => {
    // Store current view to return later
    localStorage.setItem('returnToProcessFlowTracker', 'true');
    
    // Navigate to the appropriate module
    switch (moduleName) {
      case 'dashboard':
        window.location.href = '/#dashboard';
        break;
      case 'quotation':
        window.location.href = '/#quotation';
        break;
      case 'sales-orders':
        window.location.href = '/#sales-orders';
        break;
      case 'purchase':
        window.location.href = '/#purchase';
        break;
      case 'inventory':
        window.location.href = '/#inventory';
        break;
      case 'financial':
        window.location.href = '/#financial';
        break;
      case 'chart-of-accounts':
        window.location.href = '/#chart-of-accounts';
        break;
      case 'quality':
        window.location.href = '/#quality';
        break;
      case 'hr':
        window.location.href = '/#hr';
        break;
      default:
        window.location.href = '/#dashboard';
    }
  };

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Toggle chapter expansion
  const toggleChapter = (chapterId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(chapterId)) {
      newExpanded.delete(chapterId);
    } else {
      newExpanded.add(chapterId);
    }
    setExpandedGroups(newExpanded);
  };

  // Filter tasks based on search term
  const filteredTasks = useMemo(() => {
    if (!taskSearchTerm) return teachingTasks;

    const filtered = {};
    Object.keys(teachingTasks).forEach(groupKey => {
      const group = teachingTasks[groupKey];
      const filteredChapters = group.chapters.filter(chapter => {
        const matchingTasks = chapter.tasks.filter(task =>
          task.title.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
          task.description.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
          task.id.includes(taskSearchTerm)
        );
        return matchingTasks.length > 0;
      });

      if (filteredChapters.length > 0) {
        filtered[groupKey] = {
          ...group,
          chapters: filteredChapters.map(chapter => ({
            ...chapter,
            tasks: chapter.tasks.filter(task =>
              task.title.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
              task.description.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
              task.id.includes(taskSearchTerm)
            )
          }))
        };
      }
    });

    return filtered;
  }, [taskSearchTerm]);

  // Get module icon and color (single definition)
  const getModuleIcon = (module) => {
    const moduleIcons = {
      'quotation': FileText,
      'sales-orders': ShoppingCart,
      'purchase': Package,
      'inventory': Package,
      'financial': DollarSign,
      'chart-of-accounts': Calculator,
      'quality': CheckCircle,
      'hr': User,
      'dashboard': Home
    };
    return moduleIcons[module] || FileText;
  };

  const getModuleColor = (module) => {
    const moduleColors = {
      'quotation': 'blue',
      'sales-orders': 'green',
      'purchase': 'orange',
      'inventory': 'purple',
      'financial': 'red',
      'chart-of-accounts': 'teal',
      'quality': 'emerald',
      'hr': 'pink',
      'dashboard': 'gray'
    };
    return moduleColors[module] || 'gray';
  };

  // Document conversion functions
  const fetchDocumentConversions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('process_flows')
        .select('*')
        .eq('process_type', 'CONVERSION');

      // Apply filters
      if (conversionFilters.sourceModule) {
        query = query.eq('source_module', conversionFilters.sourceModule);
      }
      if (conversionFilters.targetModule) {
        query = query.eq('target_module', conversionFilters.targetModule);
      }
      if (conversionFilters.status) {
        query = query.eq('status', conversionFilters.status);
      }
      if (conversionFilters.processType) {
        query = query.eq('process_type', conversionFilters.processType);
      }

      // Apply date range filter
      if (conversionFilters.dateRange !== 'all') {
        const now = new Date();
        let startDate;
        switch (conversionFilters.dateRange) {
          case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'week':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
          case 'quarter':
            startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
            break;
          default:
            startDate = null;
        }
        if (startDate) {
          query = query.gte('created_at', startDate.toISOString());
        }
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching document conversions:', error);
        return;
      }

      setDocumentConversions(data || []);
    } catch (error) {
      console.error('Error fetching document conversions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter conversions based on search term
  const filteredConversions = useMemo(() => {
    if (!conversionSearchTerm) return documentConversions;
    
    return documentConversions.filter(conversion => 
      conversion.source_document_id?.toLowerCase().includes(conversionSearchTerm.toLowerCase()) ||
      conversion.target_document_id?.toLowerCase().includes(conversionSearchTerm.toLowerCase()) ||
      conversion.description?.toLowerCase().includes(conversionSearchTerm.toLowerCase()) ||
      conversion.source_module?.toLowerCase().includes(conversionSearchTerm.toLowerCase()) ||
      conversion.target_module?.toLowerCase().includes(conversionSearchTerm.toLowerCase())
    );
  }, [documentConversions, conversionSearchTerm]);

  // Get conversion statistics
  const conversionStats = useMemo(() => {
    const total = documentConversions.length;
    const completed = documentConversions.filter(c => c.status === 'COMPLETED').length;
    const inProgress = documentConversions.filter(c => c.status === 'IN_PROGRESS').length;
    const failed = documentConversions.filter(c => c.status === 'FAILED').length;
    
    return {
      total,
      completed,
      inProgress,
      failed,
      successRate: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [documentConversions]);

  // Get status icon and color
  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' };
      case 'IN_PROGRESS':
        return { icon: Clock, color: 'text-blue-600', bgColor: 'bg-blue-100' };
      case 'FAILED':
        return { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' };
      default:
        return { icon: AlertCircle, color: 'text-gray-600', bgColor: 'bg-gray-100' };
    }
  };

  // Handle conversion detail view
  const handleViewConversionDetail = (conversion) => {
    setSelectedConversion(conversion);
    setShowConversionDetail(true);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setConversionFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Handle sort changes
  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Refresh conversions
  const handleRefreshConversions = () => {
    fetchDocumentConversions();
  };

  // Business cycle data
  const businessCycles = {
    sales: {
      id: 'sales',
      title: '銷售週期流程',
      titleEn: 'Sales Cycle Management',
      description: '從客戶詢價到收款完成的完整銷售流程',
      icon: ShoppingCart,
      color: 'blue',
      gradient: 'from-blue-600 to-purple-600',
      steps: [
        {
          id: 1,
          title: '客戶詢價',
          titleEn: 'Customer Inquiry',
          description: '客戶通過各種渠道提出產品或服務詢價',
          module: 'CRM',
          status: 'in-progress',
          dataFlow: '客戶資料 → 詢價記錄',
          estimatedTime: '1-3天',
          inputs: ['客戶資訊', '產品需求', '預算範圍'],
          outputs: ['詢價單', '初步報價'],
          systems: ['CRM系統', '產品目錄'],
          documents: ['客戶檔案', '詢價記錄']
        },
        {
          id: 2,
          title: '報價準備',
          titleEn: 'Quotation Preparation',
          description: '根據客戶需求準備詳細報價單',
          module: 'Quotation',
          status: 'pending',
          dataFlow: '詢價資料 → 報價單',
          estimatedTime: '1-2天',
          inputs: ['詢價資料', '產品規格', '價格政策'],
          outputs: ['正式報價單', '條款條件'],
          systems: ['報價系統', '定價引擎'],
          documents: ['報價單', '成本分析']
        },
        {
          id: 3,
          title: '報價單發送',
          titleEn: 'Quotation Sending',
          description: '將報價單發送給客戶並追蹤狀態',
          module: 'Communication',
          status: 'pending',
          dataFlow: '報價單 → 客戶確認',
          estimatedTime: '即時',
          inputs: ['報價單內容', '客戶聯絡資訊', '發送方式'],
          outputs: ['發送確認', '追蹤記錄'],
          systems: ['通訊系統', '追蹤系統'],
          documents: ['發送記錄', '狀態報告']
        },
        {
          id: 4,
          title: '客戶確認',
          titleEn: 'Customer Confirmation',
          description: '客戶確認報價並同意進行交易',
          module: 'Sales',
          status: 'pending',
          dataFlow: '客戶確認 → 銷售訂單',
          estimatedTime: '1-7天',
          inputs: ['客戶回覆', '確認條款', '特殊要求'],
          outputs: ['確認文件', '訂單準備'],
          systems: ['銷售系統', '訂單系統'],
          documents: ['確認函', '訂單草稿']
        },
        {
          id: 5,
          title: '銷售訂單',
          titleEn: 'Sales Order',
          description: '根據確認的報價創建正式銷售訂單',
          module: 'Sales Orders',
          status: 'pending',
          dataFlow: '確認報價 → 銷售訂單',
          estimatedTime: '1天',
          inputs: ['確認報價', '客戶資料', '交貨要求'],
          outputs: ['銷售訂單', '交貨排程'],
          systems: ['訂單系統', '庫存系統'],
          documents: ['訂單文件', '交貨單']
        },
        {
          id: 6,
          title: '訂單處理',
          titleEn: 'Order Processing',
          description: '處理訂單並安排生產或出貨',
          module: 'Operations',
          status: 'pending',
          dataFlow: '銷售訂單 → 處理計劃',
          estimatedTime: '1-3天',
          inputs: ['銷售訂單', '庫存狀況', '生產能力'],
          outputs: ['處理計劃', '狀態更新'],
          systems: ['生產系統', '物流系統'],
          documents: ['處理單', '進度報告']
        }
      ]
    },
    purchase: {
      id: 'purchase',
      title: '採購週期流程',
      titleEn: 'Purchase Cycle Management',
      description: '從需求識別到付款完成的完整採購流程',
      icon: Package,
      color: 'green',
      gradient: 'from-green-600 to-blue-600',
      steps: [
        {
          id: 1,
          title: '需求識別',
          titleEn: 'Requirement Identification',
          description: '識別企業的物料和服務需求',
          module: 'MRP',
          status: 'in-progress',
          dataFlow: '生產計劃 → 物料需求',
          estimatedTime: '1-2天',
          inputs: ['生產計劃', '庫存狀況', '需求預測'],
          outputs: ['需求清單', '採購建議'],
          systems: ['MRP系統', '庫存系統'],
          documents: ['需求分析', '採購計劃']
        },
        {
          id: 2,
          title: '供應商搜尋',
          titleEn: 'Supplier Search',
          description: '搜尋和評估潛在供應商',
          module: 'Supplier Management',
          status: 'pending',
          dataFlow: '需求清單 → 供應商名單',
          estimatedTime: '2-5天',
          inputs: ['需求規格', '供應商資料庫', '評估標準'],
          outputs: ['供應商名單', '評估報告'],
          systems: ['供應商管理系統', '評估系統'],
          documents: ['供應商檔案', '評估記錄']
        },
        {
          id: 3,
          title: '詢價比較',
          titleEn: 'Quotation Comparison',
          description: '向供應商詢價並比較報價',
          module: 'Purchase',
          status: 'pending',
          dataFlow: '詢價單 → 報價比較',
          estimatedTime: '3-7天',
          inputs: ['詢價單', '供應商報價', '比較標準'],
          outputs: ['報價比較表', '推薦供應商'],
          systems: ['採購系統', '比較分析系統'],
          documents: ['詢價記錄', '比較報告']
        },
        {
          id: 4,
          title: '採購訂單',
          titleEn: 'Purchase Order',
          description: '創建和發送採購訂單',
          module: 'Purchase Orders',
          status: 'pending',
          dataFlow: '報價確認 → 採購訂單',
          estimatedTime: '1天',
          inputs: ['報價確認', '供應商資料', '採購條款'],
          outputs: ['採購訂單', '訂單確認'],
          systems: ['訂單系統', '供應商系統'],
          documents: ['採購訂單', '確認函']
        },
        {
          id: 5,
          title: '訂單確認',
          titleEn: 'Order Confirmation',
          description: '供應商確認訂單並安排交貨',
          module: 'Purchase',
          status: 'pending',
          dataFlow: '採購訂單 → 交貨安排',
          estimatedTime: '1-3天',
          inputs: ['採購訂單', '供應商確認', '交貨要求'],
          outputs: ['訂單確認', '交貨排程'],
          systems: ['訂單系統', '物流系統'],
          documents: ['確認文件', '交貨單']
        },
        {
          id: 6,
          title: '貨物接收',
          titleEn: 'Goods Receipt',
          description: '接收和檢查到貨的貨物',
          module: 'Inventory',
          status: 'pending',
          dataFlow: '到貨通知 → 入庫記錄',
          estimatedTime: '1天',
          inputs: ['到貨通知', '採購訂單', '品質標準'],
          outputs: ['收貨記錄', '品質報告'],
          systems: ['庫存系統', '品質系統'],
          documents: ['收貨單', '品質檢查報告']
        }
      ]
    },
    production: {
      id: 'production',
      title: '生產週期流程',
      titleEn: 'Production Cycle Management',
      description: '從生產規劃到成本會計的完整生產流程',
      icon: Activity,
      color: 'purple',
      gradient: 'from-purple-600 to-pink-600',
      steps: [
        {
          id: 1,
          title: '生產規劃',
          titleEn: 'Production Planning',
          description: '根據銷售訂單制定生產計劃',
          module: 'Production Planning',
          status: 'in-progress',
          dataFlow: '銷售訂單 → 生產計劃',
          estimatedTime: '1-2天',
          inputs: ['銷售訂單', '生產能力', '物料可用性'],
          outputs: ['生產計劃', '排程表'],
          systems: ['生產規劃系統', '排程系統'],
          documents: ['生產計劃', '排程表']
        },
        {
          id: 2,
          title: '物料需求',
          titleEn: 'Material Requirements',
          description: '計算生產所需的物料需求',
          module: 'MRP',
          status: 'pending',
          dataFlow: '生產計劃 → 物料需求',
          estimatedTime: '1天',
          inputs: ['生產計劃', 'BOM清單', '庫存狀況'],
          outputs: ['物料需求', '採購建議'],
          systems: ['MRP系統', '庫存系統'],
          documents: ['物料需求表', '採購建議']
        },
        {
          id: 3,
          title: '生產排程',
          titleEn: 'Production Scheduling',
          description: '安排生產線和資源排程',
          module: 'Production',
          status: 'pending',
          dataFlow: '生產計劃 → 詳細排程',
          estimatedTime: '1-2天',
          inputs: ['生產計劃', '設備狀況', '人員安排'],
          outputs: ['詳細排程', '資源分配'],
          systems: ['排程系統', '資源管理系統'],
          documents: ['排程表', '資源分配表']
        },
        {
          id: 4,
          title: '生產執行',
          titleEn: 'Production Execution',
          description: '執行生產計劃並監控進度',
          module: 'Production',
          status: 'pending',
          dataFlow: '排程表 → 生產進度',
          estimatedTime: '依訂單而定',
          inputs: ['排程表', '作業指導', '品質標準'],
          outputs: ['生產進度', '品質數據'],
          systems: ['生產執行系統', '品質監控系統'],
          documents: ['生產記錄', '品質報告']
        },
        {
          id: 5,
          title: '品質控制',
          titleEn: 'Quality Control',
          description: '進行品質檢查和測試',
          module: 'Quality Control',
          status: 'pending',
          dataFlow: '生產完成 → 品質檢查',
          estimatedTime: '1-2天',
          inputs: ['生產成品', '品質標準', '檢查程序'],
          outputs: ['品質報告', '合格證書'],
          systems: ['品質管理系統', '測試系統'],
          documents: ['品質檢查報告', '合格證書']
        },
        {
          id: 6,
          title: '成品入庫',
          titleEn: 'Finished Goods Storage',
          description: '將合格成品入庫並更新庫存',
          module: 'Inventory',
          status: 'pending',
          dataFlow: '品質確認 → 庫存更新',
          estimatedTime: '1天',
          inputs: ['合格成品', '入庫單', '庫位安排'],
          outputs: ['庫存更新', '入庫確認'],
          systems: ['庫存系統', '倉儲系統'],
          documents: ['入庫單', '庫存記錄']
        },
        {
          id: 7,
          title: '成本會計',
          titleEn: 'Cost Accounting',
          description: '計算生產成本並進行成本分析',
          module: 'Cost Accounting',
          status: 'pending',
          dataFlow: '生產數據 → 成本分析',
          estimatedTime: '1-3天',
          inputs: ['生產記錄', '成本數據', '分配標準'],
          outputs: ['成本報告', '成本分析'],
          systems: ['成本會計系統', '分析系統'],
          documents: ['成本報告', '分析報告']
        }
      ]
    },
    financial: {
      id: 'financial',
      title: '財務週期流程',
      titleEn: 'Financial Cycle Management',
      description: '從交易記錄到財務報表的完整財務流程',
      icon: DollarSign,
      color: 'orange',
      gradient: 'from-orange-600 to-red-600',
      steps: [
        {
          id: 1,
          title: '交易記錄',
          titleEn: 'Transaction Recording',
          description: '記錄所有業務交易和財務活動',
          module: 'Financial',
          status: 'in-progress',
          dataFlow: '業務交易 → 會計分錄',
          estimatedTime: '即時',
          inputs: ['業務單據', '會計政策', '科目設置'],
          outputs: ['會計分錄', '交易記錄'],
          systems: ['會計系統', '交易系統'],
          documents: ['會計分錄', '交易憑證']
        },
        {
          id: 2,
          title: '日記帳',
          titleEn: 'General Journal',
          description: '按時間順序記錄所有會計分錄',
          module: 'Journal Entry',
          status: 'pending',
          dataFlow: '會計分錄 → 日記帳',
          estimatedTime: '即時',
          inputs: ['會計分錄', '審核結果', '過帳指令'],
          outputs: ['日記帳', '過帳記錄'],
          systems: ['日記帳系統', '審核系統'],
          documents: ['日記帳', '過帳記錄']
        },
        {
          id: 3,
          title: '分類帳',
          titleEn: 'General Ledger',
          description: '按科目分類匯總會計數據',
          module: 'Chart of Accounts',
          status: 'pending',
          dataFlow: '日記帳 → 分類帳',
          estimatedTime: '即時',
          inputs: ['日記帳數據', '科目結構', '匯總規則'],
          outputs: ['分類帳', '科目餘額'],
          systems: ['分類帳系統', '科目管理系統'],
          documents: ['分類帳', '科目餘額表']
        },
        {
          id: 4,
          title: '試算表',
          titleEn: 'Trial Balance',
          description: '編製試算表檢查會計平衡',
          module: 'Financial',
          status: 'pending',
          dataFlow: '分類帳 → 試算表',
          estimatedTime: '1天',
          inputs: ['分類帳餘額', '平衡檢查', '調整項目'],
          outputs: ['試算表', '平衡報告'],
          systems: ['試算表系統', '平衡檢查系統'],
          documents: ['試算表', '平衡檢查報告']
        },
        {
          id: 5,
          title: '調整分錄',
          titleEn: 'Adjusting Entries',
          description: '進行期末調整和應計項目處理',
          module: 'Financial',
          status: 'pending',
          dataFlow: '試算表 → 調整分錄',
          estimatedTime: '1-2天',
          inputs: ['試算表', '調整項目', '會計政策'],
          outputs: ['調整分錄', '調整後餘額'],
          systems: ['調整系統', '政策管理系統'],
          documents: ['調整分錄', '調整報告']
        },
        {
          id: 6,
          title: '財務報表',
          titleEn: 'Financial Statements',
          description: '編製資產負債表、損益表等財務報表',
          module: 'Financial',
          status: 'pending',
          dataFlow: '調整後數據 → 財務報表',
          estimatedTime: '2-3天',
          inputs: ['調整後餘額', '報表格式', '披露要求'],
          outputs: ['財務報表', '分析報告'],
          systems: ['報表系統', '分析系統'],
          documents: ['資產負債表', '損益表', '現金流量表']
        }
      ]
    }
  };

  // Handle cycle selection
  const handleCycleSelect = (cycleId) => {
    setSelectedCycle(cycleId);
    setShowCycleDetails(true);
  };

  // Handle cycle control actions
  const handleCycleControl = (action) => {
    switch (action) {
      case 'play':
        // Play cycle animation (placeholder)
        console.log('Playing cycle:', selectedCycle);
        break;
      case 'reset':
        setSelectedCycle('sales');
        setShowCycleDetails(true);
        break;
      case 'hide':
        setShowCycleDetails(!showCycleDetails);
        break;
      default:
        break;
    }
  };

  // Fetch document conversions when view changes
  useEffect(() => {
    if (activeView === 'conversions') {
      fetchDocumentConversions();
    }
  }, [activeView, conversionFilters, sortBy, sortOrder]);

  // Render tasks (teaching) view
  const renderTasksView = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">教學任務 (Teaching Tasks)</h2>
          <p className="text-gray-600 mt-1">會計和ERP系統學習指南</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索任務..."
              value={taskSearchTerm}
              onChange={(e) => setTaskSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Task Groups */}
      <div className="space-y-6">
        {Object.entries(filteredTasks).map(([groupKey, group]) => {
          const GroupIcon = group.icon;
          const isGroupExpanded = expandedGroups.has(groupKey);
          
          return (
            <div key={groupKey} className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Group Header */}
              <div 
                className={`p-6 cursor-pointer transition-colors ${
                  isGroupExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => toggleGroup(groupKey)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg bg-${group.color}-100`}>
                      <GroupIcon className={`h-6 w-6 text-${group.color}-600`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800">{group.title}</h3>
                      <p className="text-gray-600">{group.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {group.chapters.reduce((total, chapter) => total + chapter.tasks.length, 0)} 個任務
                    </span>
                    {isGroupExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-500" />
                    )}
                  </div>
                </div>
              </div>

              {/* Group Content */}
              {isGroupExpanded && (
                <div className="border-t border-gray-200">
                  {group.chapters.map((chapter) => {
                    const isChapterExpanded = expandedGroups.has(chapter.id);
                    
                    return (
                      <div key={chapter.id} className="border-b border-gray-100 last:border-b-0">
                        {/* Chapter Header */}
                        <div 
                          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => toggleChapter(chapter.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-800">{chapter.title}</h4>
                              <p className="text-sm text-gray-600">{chapter.subtitle}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-500">
                                {chapter.tasks.length} 個任務
                              </span>
                              {isChapterExpanded ? (
                                <ChevronDown className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-500" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Chapter Tasks */}
                        {isChapterExpanded && (
                          <div className="bg-gray-50">
                            {chapter.tasks.map((task) => {
                              const TaskModuleIcon = getModuleIcon(task.module);
                              const moduleColor = getModuleColor(task.module);
                              
                              return (
                                <div key={task.id} className="p-4 border-b border-gray-200 last:border-b-0 hover:bg-white transition-colors">
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-3">
                                        <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                          {task.id}
                                        </span>
                                        <h5 className="font-medium text-gray-800">{task.title}</h5>
                                      </div>
                                      <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <button
                                        onClick={() => navigateToModule(task.module)}
                                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-${moduleColor}-100 text-${moduleColor}-700 hover:bg-${moduleColor}-200`}
                                      >
                                        <TaskModuleIcon className="h-4 w-4" />
                                        <span>前往模組</span>
                                        <ExternalLink className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Learning Tips */}
      <div className="bg-blue-50 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <GraduationCap className="h-6 w-6 text-blue-600 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-blue-800 mb-2">學習提示</h3>
            <ul className="text-blue-700 space-y-1">
              <li>• 點擊任務旁的「前往模組」按鈕可直接跳轉到相關ERP模組</li>
              <li>• 使用搜索功能快速找到特定任務或概念</li>
              <li>• 展開章節查看詳細的學習任務</li>
              <li>• 每個任務都與實際的ERP功能相對應</li>
              <li>• 完成任務後可返回此頁面繼續學習</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render conversions view
  const renderConversionsView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold mb-2">文件轉換追蹤</h2>
            <p className="text-lg opacity-90 mb-4">監控和審計跨模組文件轉換流程</p>
            <div className="text-sm opacity-80">
              <span>總轉換: {conversionStats.total}</span>
              <span className="mx-4">成功率: {conversionStats.successRate}%</span>
              <span>進行中: {conversionStats.inProgress}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm opacity-80">最後更新: {new Date().toLocaleString('zh-TW')}</div>
            <button
              onClick={handleRefreshConversions}
              className="mt-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>刷新</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">總轉換數</p>
              <p className="text-2xl font-bold text-gray-800">{conversionStats.total}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Database className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">已完成</p>
              <p className="text-2xl font-bold text-green-600">{conversionStats.completed}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">進行中</p>
              <p className="text-2xl font-bold text-blue-600">{conversionStats.inProgress}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">失敗</p>
              <p className="text-2xl font-bold text-red-600">{conversionStats.failed}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜索文件轉換..."
                value={conversionSearchTerm}
                onChange={(e) => setConversionSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Source Module Filter */}
          <div>
            <select
              value={conversionFilters.sourceModule}
              onChange={(e) => handleFilterChange('sourceModule', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">來源模組</option>
              <option value="quotation">報價單</option>
              <option value="sales_order">銷售訂單</option>
              <option value="purchase_order">採購訂單</option>
              <option value="inventory">庫存</option>
            </select>
          </div>

          {/* Target Module Filter */}
          <div>
            <select
              value={conversionFilters.targetModule}
              onChange={(e) => handleFilterChange('targetModule', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">目標模組</option>
              <option value="sales_order">銷售訂單</option>
              <option value="purchase_order">採購訂單</option>
              <option value="inventory">庫存</option>
              <option value="financial">財務</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={conversionFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">狀態</option>
              <option value="COMPLETED">已完成</option>
              <option value="IN_PROGRESS">進行中</option>
              <option value="FAILED">失敗</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div>
            <select
              value={conversionFilters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">所有時間</option>
              <option value="today">今天</option>
              <option value="week">本週</option>
              <option value="month">本月</option>
              <option value="quarter">本季</option>
            </select>
          </div>
        </div>
      </div>

      {/* Conversions Table */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">轉換記錄</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleSortChange('created_at')}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
              >
                <span>時間</span>
                {sortBy === 'created_at' ? (
                  sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />
                ) : <ArrowUpDown className="h-4 w-4" />}
              </button>
              <button
                onClick={() => handleSortChange('status')}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
              >
                <span>狀態</span>
                {sortBy === 'status' ? (
                  sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />
                ) : <ArrowUpDown className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">載入中...</p>
          </div>
        ) : filteredConversions.length === 0 ? (
          <div className="p-8 text-center">
            <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">沒有找到轉換記錄</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">流程ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">來源文件</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">目標文件</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">開始時間</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">完成時間</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredConversions.map((conversion) => {
                  const statusInfo = getStatusIcon(conversion.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <tr key={conversion.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{conversion.flow_id}</div>
                        <div className="text-sm text-gray-500">{conversion.process_type}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="p-1 bg-blue-100 rounded">
                            <FileText className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{conversion.source_document_id}</div>
                            <div className="text-sm text-gray-500">{conversion.source_module}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="p-1 bg-green-100 rounded">
                            <FileCheck className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{conversion.target_document_id}</div>
                            <div className="text-sm text-gray-500">{conversion.target_module}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {conversion.status === 'COMPLETED' ? '已完成' : 
                           conversion.status === 'IN_PROGRESS' ? '進行中' : 
                           conversion.status === 'FAILED' ? '失敗' : conversion.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {conversion.initiated_at ? new Date(conversion.initiated_at).toLocaleString('zh-TW') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {conversion.completed_at ? new Date(conversion.completed_at).toLocaleString('zh-TW') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewConversionDetail(conversion)}
                          className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                        >
                          <Eye className="h-4 w-4" />
                          <span>查看</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Conversion Detail Modal */}
      {showConversionDetail && selectedConversion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">轉換詳情</h3>
                <button
                  onClick={() => setShowConversionDetail(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">基本信息</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">流程ID:</span>
                      <span className="font-medium">{selectedConversion.flow_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">流程類型:</span>
                      <span className="font-medium">{selectedConversion.process_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">狀態:</span>
                      <span className="font-medium">{selectedConversion.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">發起人:</span>
                      <span className="font-medium">{selectedConversion.initiated_by}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">時間信息</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">開始時間:</span>
                      <span className="font-medium">{selectedConversion.initiated_at ? new Date(selectedConversion.initiated_at).toLocaleString('zh-TW') : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">完成時間:</span>
                      <span className="font-medium">{selectedConversion.completed_at ? new Date(selectedConversion.completed_at).toLocaleString('zh-TW') : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">創建時間:</span>
                      <span className="font-medium">{selectedConversion.created_at ? new Date(selectedConversion.created_at).toLocaleString('zh-TW') : '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">更新時間:</span>
                      <span className="font-medium">{selectedConversion.updated_at ? new Date(selectedConversion.updated_at).toLocaleString('zh-TW') : '-'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-3">來源文件</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-600">文件ID:</span>
                      <span className="font-medium">{selectedConversion.source_document_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600">模組:</span>
                      <span className="font-medium">{selectedConversion.source_module}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-600">類型:</span>
                      <span className="font-medium">{selectedConversion.source_document_type}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-3">目標文件</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-600">文件ID:</span>
                      <span className="font-medium">{selectedConversion.target_document_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-600">模組:</span>
                      <span className="font-medium">{selectedConversion.target_module}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-600">類型:</span>
                      <span className="font-medium">{selectedConversion.target_document_type}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description and Metadata */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3">描述</h4>
                  <p className="text-sm text-gray-600">{selectedConversion.description || '無描述'}</p>
                </div>
                
                {selectedConversion.metadata && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3">元數據</h4>
                    <pre className="text-sm text-gray-600 bg-white p-3 rounded border overflow-x-auto">
                      {JSON.stringify(selectedConversion.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConversionDetail(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  關閉
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render content based on active view
  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <div className="space-y-8">
            {/* Enhanced Header with Learning Center Style */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-8 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold mb-2">典型ERP流程學習</h2>
                  <p className="text-lg opacity-90 mb-4">Typical ERP Flow Learning Center</p>
                  <p className="text-sm opacity-80">企業資源規劃系統核心業務流程學習平台</p>
                </div>
                <div className="flex items-center space-x-4">
                  <button className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2">
                    <BookOpen className="h-4 w-4" />
                    <span>學習模式</span>
                  </button>
                  <div className="relative">
                    <button className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2">
                      <span>互動學習</span>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Core Business Cycles Overview - Learning Center Style */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(businessCycles).map(([cycleId, cycle]) => {
                const CycleIcon = cycle.icon;
                const isSelected = selectedCycle === cycleId;
                const colorClasses = {
                  blue: 'border-blue-500 bg-blue-50',
                  green: 'border-green-500 bg-green-50',
                  purple: 'border-purple-500 bg-purple-50',
                  orange: 'border-orange-500 bg-orange-50'
                };
                const iconColorClasses = {
                  blue: 'bg-blue-100 text-blue-600',
                  green: 'bg-green-100 text-green-600',
                  purple: 'bg-purple-100 text-purple-600',
                  orange: 'bg-orange-100 text-orange-600'
                };
                const badgeColorClasses = {
                  blue: 'bg-blue-100 text-blue-800',
                  green: 'bg-green-100 text-green-800',
                  purple: 'bg-purple-100 text-purple-800',
                  orange: 'bg-orange-100 text-orange-800'
                };

                return (
                  <div
                    key={cycleId}
                    onClick={() => handleCycleSelect(cycleId)}
                    className={`bg-white rounded-lg shadow-lg border-2 hover:shadow-xl transition-all duration-300 cursor-pointer ${
                      isSelected ? colorClasses[cycle.color] : `border-${cycle.color}-500`
                    }`}
                  >
                    <div className="p-6 text-center">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                        isSelected ? iconColorClasses[cycle.color] : iconColorClasses[cycle.color]
                      }`}>
                        <CycleIcon className="h-8 w-8" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800 mb-2">{cycle.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{cycle.titleEn}</p>
                      <div className="text-xs text-gray-500">
                        <span className={`px-2 py-1 rounded ${badgeColorClasses[cycle.color]}`}>
                          {cycle.steps.length} 步驟
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Selected Cycle Detail - Dynamic */}
            {selectedCycle && businessCycles[selectedCycle] && (
              <div className={`bg-gradient-to-r ${businessCycles[selectedCycle].gradient} rounded-lg p-8 text-white`}>
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      {(() => {
                        const CycleIcon = businessCycles[selectedCycle].icon;
                        return <CycleIcon className="h-8 w-8" />;
                      })()}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{businessCycles[selectedCycle].title}</h3>
                      <p className="text-lg opacity-90">{businessCycles[selectedCycle].titleEn}</p>
                      <p className="text-sm opacity-80 mt-2">{businessCycles[selectedCycle].description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => handleCycleControl('play')}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Play className="h-4 w-4" />
                      <span>播放</span>
                    </button>
                    <button 
                      onClick={() => handleCycleControl('reset')}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>重置</span>
                    </button>
                    <button 
                      onClick={() => handleCycleControl('hide')}
                      className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Eye className="h-4 w-4" />
                      <span>隱藏詳情</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Process Steps - Dynamic */}
            {selectedCycle && businessCycles[selectedCycle] && (
              <div className="space-y-6">
                {businessCycles[selectedCycle].steps.map((step, index) => {
                  const stepColors = {
                    blue: 'bg-blue-500',
                    green: 'bg-green-500',
                    purple: 'bg-purple-500',
                    orange: 'bg-orange-500'
                  };
                  const statusColors = {
                    'in-progress': 'bg-blue-100 text-blue-800',
                    'pending': 'bg-gray-100 text-gray-800',
                    'completed': 'bg-green-100 text-green-800'
                  };
                  
                  return (
                    <div key={step.id} className="bg-white rounded-lg shadow-lg border border-gray-200">
                      <div className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className={`w-12 h-12 ${stepColors[businessCycles[selectedCycle].color]} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                            {step.id}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="text-lg font-bold text-gray-800">{step.title}</h4>
                                <p className="text-sm text-gray-600">{step.titleEn}</p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500">模組: {step.module}</span>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[step.status]}`}>
                                  <Clock className="h-3 w-3 mr-1" />
                                  {step.status === 'in-progress' ? '進行中' : 
                                   step.status === 'completed' ? '已完成' : '待處理'}
                                </span>
                              </div>
                            </div>
                            
                            <p className="text-gray-700 mb-4">{step.description}</p>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h5 className="font-semibold text-gray-800 mb-2">輸入資料</h5>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {step.inputs.map((input, idx) => (
                                    <li key={idx}>• {input}</li>
                                  ))}
                                </ul>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h5 className="font-semibold text-gray-800 mb-2">輸出結果</h5>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {step.outputs.map((output, idx) => (
                                    <li key={idx}>• {output}</li>
                                  ))}
                                </ul>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h5 className="font-semibold text-gray-800 mb-2">相關系統</h5>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {step.systems.map((system, idx) => (
                                    <li key={idx}>• {system}</li>
                                  ))}
                                </ul>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <h5 className="font-semibold text-gray-800 mb-2">產生文件</h5>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {step.documents.map((document, idx) => (
                                    <li key={idx}>• {document}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                            
                            <div className="mt-4 flex items-center justify-between">
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span>數據流: {step.dataFlow}</span>
                                <span>預計時間: {step.estimatedTime}</span>
                              </div>
                              <button className={`${stepColors[businessCycles[selectedCycle].color].replace('bg-', 'bg-').replace('-500', '-600')} text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors`}>
                                前往模組
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Learning Tips */}
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <div className="flex items-start space-x-3">
                <GraduationCap className="h-6 w-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">學習提示</h3>
                  <ul className="text-blue-700 space-y-1">
                    <li>• 點擊步驟卡片可查看詳細的流程說明</li>
                    <li>• 使用播放按鈕可觀看完整的流程演示</li>
                    <li>• 每個步驟都包含輸入、輸出、相關系統和產生文件</li>
                    <li>• 預計時間幫助了解各步驟的時間安排</li>
                    <li>• 狀態指示器顯示當前步驟的進度</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      case 'tasks':
        return renderTasksView();
      case 'conversions':
        return renderConversionsView();
      default:
        return renderTasksView();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Navigation */}
        <div className="mb-6">
          <div className="flex space-x-4">
            <button 
              onClick={() => setActiveView('dashboard')}
              className={`px-4 py-2 rounded-lg ${activeView === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              儀表板
            </button>
            <button 
              onClick={() => setActiveView('tasks')}
              className={`px-4 py-2 rounded-lg ${activeView === 'tasks' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              教學任務
            </button>
            <button 
              onClick={() => setActiveView('conversions')}
              className={`px-4 py-2 rounded-lg ${activeView === 'conversions' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              文件轉換
            </button>
          </div>
        </div>
        
        {renderContent()}
      </div>
    </div>
  );
};

export default ProcessFlowTracker;
